import axios, { AxiosError } from "axios";
import type { Opaque } from "type-fest";
import { BASE_URLS } from "config";
import { getEnv } from "env";

type SfdcToken = Opaque<string>;

// --- PKCE Utilities ---

const PKCE_VERIFIER_KEY = "splat_pkce_code_verifier";

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

async function generatePkceCodes(): Promise<{
  codeVerifier: string;
  codeChallenge: string;
}> {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  const codeVerifier = base64UrlEncode(array);
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(codeVerifier)
  );
  const codeChallenge = base64UrlEncode(new Uint8Array(digest));
  return { codeVerifier, codeChallenge };
}

function storePkceVerifier(codeVerifier: string): void {
  localStorage.setItem(PKCE_VERIFIER_KEY, codeVerifier);
}

function retrieveAndClearPkceVerifier(): string | null {
  const verifier = localStorage.getItem(PKCE_VERIFIER_KEY);
  localStorage.removeItem(PKCE_VERIFIER_KEY);
  return verifier;
}
type ErrorResponseData = {
  statusCode?: number;
  errorCode: string;
  message: string;
};

const isRefreshTokenExpiredAxiosError = (e: AxiosError): boolean => {
  const responseData = e.response?.data as ErrorResponseData | undefined;
  if (!responseData) {
    return false;
  }
  return (
    responseData.errorCode === "invalid_grant" &&
    responseData.message.toLowerCase().includes("token validity expired")
  );
};

type GetTokenFromRefreshTokenResult =
  | {
      success: true;
      token: SfdcToken;
      tokenType: TokenType;
      refreshToken?: string;
    }
  | {
      success: false;
      error: string;
      reason: "refresh_token_expired" | "request_failed";
    };

const determineErrorFromResponseData = (e: AxiosError) => {
  const responseData = e.response?.data as ErrorResponseData;
  if (responseData) {
    if (responseData.message.includes("INVALID_LOGIN")) {
      throw new Error("Incorrect username or password.", { cause: e });
    } else if (
      responseData.message
        .toLowerCase()
        .includes("user is not assigned to the client application")
    ) {
      throw new Error(
        "Logging in with Okta is not yet available to your user group. Please continue to log in with Salesforce.",
        { cause: e }
      );
    }
  }
};

const getToken = async (username: string, password: string) => {
  const env = getEnv();
  let response;
  try {
    response = await axios.post(
      `${BASE_URLS.AUTH_SERVICE[env]}/login/credentials`,
      {
        username,
        password,
      }
    );
  } catch (e) {
    const error = e as AxiosError;
    determineErrorFromResponseData(error);
    throw e;
  }

  return response.data as { token: SfdcToken; tokenType: TokenType };
};

const getTokenFromRefreshToken = async (
  refreshToken: string
): Promise<GetTokenFromRefreshTokenResult> => {
  const env = getEnv();
  try {
    const response = await axios.post(
      `${BASE_URLS.AUTH_SERVICE[env]}/login/refresh`,
      { refreshToken }
    );
    const data = response.data as {
      token: SfdcToken;
      tokenType: TokenType;
      refreshToken?: string;
    };
    return {
      success: true,
      token: data.token,
      tokenType: data.tokenType,
      refreshToken: data.refreshToken,
    };
  } catch (e) {
    const error = e as AxiosError;
    if (isRefreshTokenExpiredAxiosError(error)) {
      return {
        success: false,
        error: "Refresh token expired.",
        reason: "refresh_token_expired",
      };
    }
    const responseData = error.response?.data as ErrorResponseData | undefined;
    return {
      success: false,
      error: responseData?.message ?? "Unable to refresh session.",
      reason: "request_failed",
    };
  }
};

const getOauthAuthorizeUrl = async (
  codeChallenge: string,
  isD2H: boolean,
  isPartner: boolean
): Promise<{ authorizeUrl: string }> => {
  const env = getEnv();
  const params = new URLSearchParams({
    codeChallenge,
    codeChallengeMethod: "S256",
    isD2H: isD2H.toString(),
    isPartner: isPartner.toString(),
  });
  const response = await axios.get(
    `${BASE_URLS.AUTH_SERVICE[env]}/login/oauth/url?${params}`
  );
  return response.data as { authorizeUrl: string };
};

const exchangeAuthCode = async (
  code: string,
  codeVerifier: string
): Promise<{ sfdcToken: SfdcToken; refreshToken?: string }> => {
  const env = getEnv();
  const response = await axios.post(
    `${BASE_URLS.AUTH_SERVICE[env]}/login/oauth/exchangeCode`,
    { code, codeVerifier }
  );
  return response.data as { sfdcToken: SfdcToken; refreshToken?: string };
};

export type { SfdcToken, GetTokenFromRefreshTokenResult };
export {
  getToken,
  getTokenFromRefreshToken,
  getOauthAuthorizeUrl,
  exchangeAuthCode,
  generatePkceCodes,
  storePkceVerifier,
  retrieveAndClearPkceVerifier,
};
